// Remembers the page a logged-out visitor was on so that logging in brings
// them straight back to it (e.g. a shared profile link) instead of "/".
//
// Two sources, in priority order:
//   1. an explicit `?redirect=/some/path` on the login URL
//   2. the last normal page this tab visited while logged out (sessionStorage)

const KEY = "sp_return_to";

// Pages that must never be a post-login destination.
const EXCLUDED_PREFIXES = [
  "/login",
  "/signup",
  "/register",
  "/create-account",
  "/otpsender",
  "/setotp",
  "/forgot-password",
  "/reset-password",
  "/updatepassword",
  "/verify-email",
  "/claim/",
  "/edit-profile",
  "/admin",
  "/support-app",
  "/payment",
];

// Only same-site relative paths ("/x"), never "//evil.com" or full URLs.
export const safePath = (path) => {
  if (typeof path !== "string") return null;
  const p = path.trim();
  if (!p.startsWith("/") || p.startsWith("//") || p.startsWith("/\\")) return null;
  if (p === "/") return null;
  if (EXCLUDED_PREFIXES.some((x) => p === x || p.startsWith(x.endsWith("/") ? x : `${x}/`) || p.startsWith(`${x}?`))) {
    return null;
  }
  return p;
};

export const rememberReturnTo = (path) => {
  const p = safePath(path);
  if (!p) return;
  try {
    sessionStorage.setItem(KEY, p);
  } catch {
    /* storage disabled */
  }
};

// Login link that carries the current page along.
export const loginUrlWithReturn = (path) => {
  const p = safePath(path);
  return p ? `/login?redirect=${encodeURIComponent(p)}` : "/login";
};

// Where to send someone right after a successful login. Clears the memory.
export const consumeReturnTo = () => {
  let target = null;
  try {
    const q = new URLSearchParams(window.location.search).get("redirect");
    target = safePath(q);
  } catch {
    /* ignore */
  }
  try {
    if (!target) target = safePath(sessionStorage.getItem(KEY));
    sessionStorage.removeItem(KEY);
  } catch {
    /* storage disabled */
  }
  return target || "/";
};

// Admins keep their usual landing (dashboard via "/"); members go back.
export const postLoginPath = (role) => {
  const target = consumeReturnTo();
  if (role === "admin" || role === "child_admin") return "/";
  return target;
};

// Called on every route change while logged out: remembers the page the
// visitor is on, so the Login button in the navbar also brings them back.
// Auth pages are ignored (so /login keeps pointing at the page before it);
// the home page clears the memory, since "/" is the default anyway.
export const trackGuestPage = (path) => {
  const p = safePath(path);
  try {
    if (p) {
      sessionStorage.setItem(KEY, p);
    } else if (typeof path === "string" && (path === "/" || path.startsWith("/?"))) {
      sessionStorage.removeItem(KEY);
    }
  } catch {
    /* storage disabled */
  }
};
